import React, { useEffect, useRef, useState } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import {
  ClientPlatformCode,
  getImageName,
  ApiConfigData,
  GetImageUrl,
  handleRedirectWithLoading,
  ModalOrSso,
  SectionContentMasterData,
  SectionMasterDataBySectionId,
  trackEvent,
  UserIdAndStatusData,
  DefaultCardName,
} from "../../Utility/Utility";
import { useLocation, useNavigate } from "react-router-dom";
import DisclaimerPopup from "../../Components/SupportingBanner/DisclaimerPopup";

const WebTopOfferBanner = ({
  isFullWidth,
  sectionId,
  setShowModal,
  login,
  setIsLoading,
  isOfferListPage,
  sectionData,
  setIsSupportBanner,
  isMobileView
}) => {
  const navigate = useNavigate();
  const { clientCode, platformCode } = ClientPlatformCode();
  const { userId, userStatus } = UserIdAndStatusData();
  const defaultCardName = DefaultCardName();
  const { sctionContentdata } = SectionContentMasterData();
  const contentData = sctionContentdata.filter(
    (content) => content.sectionMasterId === sectionId
  );
  const [disclaimerPopup, setDisclaimerPopup] = useState(false);
  const [bannerData, setBannerData] = useState(null);
  const sliderRef = useRef(null);
  const sectionRef = useRef(null);
  const activeSlideRef = useRef(0);
  const trackedSlideIndexesRef = useRef(new Set());
  const [visible, setVisible] = useState(true);
  const isModal = ModalOrSso();

  const apiConfigData = ApiConfigData();
  const integrationId = apiConfigData?.find(
    (data) => data?.paramsKey === "integrationId"
  )?.paramsValue;
  const loginUrl = `${process.env.REACT_APP_INTEGRATION_LOGIN_URL}Login/${integrationId}`;

  useEffect(() => {
    if (contentData?.length) {
      setIsSupportBanner(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentData?.length]);

  const eventTrackingOnView = (data) => {
    let bannerName = getImageName(data?.webImage);
    trackEvent("view_promotion", {
      client_code: clientCode,
      platform_code: platformCode,
      user_id: userId,
      user_status: userStatus,
      card_name: defaultCardName,
      page_type: window.location.pathname === "/" ? "home_page" : "my_account_page",
      ecommerce: {
        creative_name: bannerName,
        creative_slot: `${sectionData?.displayOrder}-${data?.displayOrder}`,
        promotion_id: String(data?.id),
        promotion_name: sectionData?.text?.trim() || sectionData?.sectionName,
        is_personalized: data?.isPersonalized ? "true" : "false"
      }
    });
  };

  const isSectionInViewport = () => {
    if (!sectionRef.current) return false;
    const rect = sectionRef.current.getBoundingClientRect();
    return rect.top < window.innerHeight && rect.bottom > 0;
  };

  const triggerViewEventForSlide = (slideIndex) => {
    if (!contentData?.length) return;

    const normalizedIndex =
      ((slideIndex % contentData.length) + contentData.length) %
      contentData.length;

    if (trackedSlideIndexesRef.current.has(normalizedIndex)) return;
    if (!isSectionInViewport()) return;

    const content = contentData[normalizedIndex];
    if (!content) return;

    eventTrackingOnView(content);
    trackedSlideIndexesRef.current.add(normalizedIndex);
  };

  const webTopOfferTrackingKey = contentData
    ?.map((content) => content?.id ?? content?.webImage ?? content?.mobImage)
    .join("|");

  useEffect(() => {
    if (!contentData?.length) return;

    trackedSlideIndexesRef.current = new Set();
    activeSlideRef.current = 0;

    const checkVisibility = () => {
      if (!isSectionInViewport()) return;

      if (contentData.length <= 2) {
        contentData.forEach((_, index) => {
          triggerViewEventForSlide(index);
        });
      } else {
        triggerViewEventForSlide(activeSlideRef.current);
      }
    };

    // Check immediately after render in case section is already in viewport
    const timer = setTimeout(() => checkVisibility(), 150);

    window.addEventListener('scroll', checkVisibility, { passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', checkVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webTopOfferTrackingKey]);

  if (!visible || !contentData.length) return null;
  const handleScrollLeft = () => {
    sliderRef.current?.slickPrev();
  };
  const handleScrollRight = () => {
    sliderRef.current?.slickNext();
  };
  const handleClick = (cta, contentData) => {
    let bannerName = getImageName(contentData?.webImage);
    trackEvent("select_promotion", {
      client_code: clientCode,
      platform_code: platformCode,
      user_id: userId,
      user_status: userStatus,
      card_name: defaultCardName,
      page_type: window.location.pathname === "/" ? "home_page" : "my_account_page",
      ecommerce: {
        creative_name: bannerName, // ex: Name of the featured banner 
        creative_slot: `${sectionData?.displayOrder}-${contentData?.displayOrder}`,   //The position of the banner in the grid ex: 1-1,1-2 
        promotion_id: String(contentData?.id),  // Unique Banner id for displayed banner
        promotion_name: sectionData?.text?.trim() || sectionData?.sectionName, // Unique Banner title of displayed banner; ex: Featured for you
        is_personalized: contentData?.isPersonalized ? "true" : "false"
      }
    });
    const { isStaccOffer, disclaimerContent } = contentData;
    if (!isStaccOffer) {
      if (cta && disclaimerContent) {
        setBannerData(contentData);
        setDisclaimerPopup(true);
      }
      return;
    }
    if (cta) {
      handleRedirectWithLoading(
        setIsLoading,
        cta,
        login,
        setShowModal,
        isModal,
        navigate,
        cta
      );
    }
  };

  const settings = {
    dots: true,
    infinite: true,
    speed: 800,
    slidesToShow: 2,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    arrows: false,
    appendDots: (dots) => (
      <div className="carousel-dots">
        <ul>{dots}</ul>
      </div>
    ),
    customPaging: () => <div className="dot"></div>,
    afterChange: (currentSlide) => {
      activeSlideRef.current = currentSlide;
      triggerViewEventForSlide(currentSlide);
    },
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          autoplay: true,
        },
      },
    ],
  };

  return (
    <div className="mt-4" ref={sectionRef}>
      {contentData.length === 1 ? (
        <div
          id={`CONT_${contentData[0]?.id}`}
          className={` mt-5 pb-6 relative ${isFullWidth ? "px-0" : "px-6 container mx-auto"
            }`}
        >
          <i
            className="fa-solid fa-xmark absolute top-4 right-12 text-2xl z-10 cursor-pointer"
            onClick={() => setVisible(false)}
          />
          <img
            onClick={() => handleClick(contentData[0]?.cta, contentData[0])}
            src={GetImageUrl(contentData[0]?.webImage)}
            alt="banner-single"
            className={`w-full cursor-pointer ${!isFullWidth ? "rounded-2xl" : ""
              }`}
          />
        </div>
      ) : (
        ""
      )}

      {contentData.length === 2 ? (
        <div
          className={`container mx-auto flex justify-center gap-4 mt-5 pb-6 ${isFullWidth ? "px-0" : "px-6"
            }`}
        >
          {contentData.map((conData, index) => (
            <img
              id={`CONT_${conData?.id}`}
              key={index}
              onClick={() => handleClick(conData?.cta, conData)}
              src={GetImageUrl(conData?.webImage)}
              alt={`banner-${index}`}
              className={`w-1/2 cursor-pointer ${!isFullWidth ? "rounded-2xl" : "overflow-hidden"
                }`}
            />
          ))}
        </div>
      ) : (
        ""
      )}

      {contentData.length > 2 ? (
        <div
          className={`container mx-auto mt-4 pb-6 relative ${isFullWidth ? "px-0" : "px-4"
            }`}
        >
          {isOfferListPage && (
            <>
              <button
                className="absolute left-0 top-[40%] transform -translate-y-1/2 bg-white rounded-full p-2 shadow-md z-10 flex items-center justify-center w-10 h-10"
                aria-label="Scroll Left"
                onClick={handleScrollLeft}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="w-5 h-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
                  />
                </svg>
              </button>

              <button
                className="absolute right-0 top-[40%] transform -translate-y-1/2 bg-white rounded-full p-2 shadow-md z-10 flex items-center justify-center w-10 h-10"
                aria-label="Scroll Right"
                onClick={handleScrollRight}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="w-5 h-5 transform scale-x-[-1]"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
                  />
                </svg>
              </button>
            </>
          )}

          {/* Slider */}
          <Slider {...settings} ref={sliderRef}>
            {contentData.map((conData, index) => (
              <div id={`CONT_${conData?.id}`} key={index} className="px-2">
                <img
                  onClick={() => handleClick(conData?.cta, conData)}
                  src={GetImageUrl(conData?.webImage)}
                  alt={`banner-${index}`}
                  className={`w-full cursor-pointer ${!isFullWidth ? "rounded-2xl" : ""
                    }`}
                />
              </div>
            ))}
          </Slider>
        </div>
      ) : (
        ""
      )}
      {disclaimerPopup && (
        <>
          <DisclaimerPopup
            onClose={() => setDisclaimerPopup(false)}
            isOpen={disclaimerPopup}
            bannerData={bannerData}
            isMobileView={isMobileView}
          />
        </>
      )}
    </div>
  );
};

export default WebTopOfferBanner;
